import { API_BASE, useMemberGetQuery } from "modules/members/api";
import { useQuery } from "@tanstack/react-query";
import { MFApp } from "./apps";
import { NumberDecrementStepperProps } from "@chakra-ui/react";

export type MFIdentity = {
  id: string;
  extId: string;
  username?: string;
  displayName?: string;
  intId: string;
  intImageUrl: string;
  type?: string;
};

export type MFMember = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name: string;
  imageUrl?: string;
  insertedAt: string;
  updatedAt: string;
  jobTitle?: string;
  status: "active" | "inactive";
  department: string;
  groups: MFGroup[];
  isLicensed: boolean;
  featureFlags?: string[];
  activeIdentities: MFIdentity[];
  requestableNhis?: MFIdentity[];
  accessReviewDefaultView?: "entitlements" | "suggestions";
};

export type MFGroup = {
  id: string;
  name: string;
  source: "microsoft" | "google" | "okta";
  extId: string;
};

export interface MFMemberAccount {
  id: string;
  created: string;
  authType: "email_pw" | "oauth" | "okta" | "unknown";
  lastDetectedSignIn?: string;
  insertedAt: string;
  updatedAt: string;
  requiredStatus: "member_required" | "member_not_required";
  memberId: string;
  appId: string;
  app?: MFApp;
  status: "active" | "removed" | "unknown";
  loginStatus: "active" | "unknown" | "disabled";
  member?: MFMember;
  mfaStatus?: "enabled" | "disabled" | "unknown";
}

export type LoginProvider = "google" | "microsoft" | "okta";

export type MFPortalConfig = {
  orgId: string;
  logoUrl: string;
  newPortalEnabled: boolean;
  availableLoginOptions: LoginProvider[];
  /**
   * The same list as the authed `/members/portal-config` names it
   * (XWeb.Members.OrgView); `availableLoginOptions` is the unauthed spelling.
   */
  loginOptions?: LoginProvider[];
  subdomain: string;
  /**
   * Primary nav destinations this org shows (see `components/nav/navConfig`
   * for the ids). Absent on the unauthed by-subdomain config, which only
   * serves the login screen.
   */
  navItems?: string[];
  /**
   * Whether the header offers the "Request access" button. Absent on the
   * unauthed by-subdomain config, and on an org whose row predates the setting
   * — both mean "show it" (see `navVisibility.isRequestAccessVisible`).
   */
  requestAccessEnabled?: boolean;
};

export type MFMemberStats = {
  tasks: number;
  provisioning: number;
  deprovisioning: number;
  entitlementAdjustment: number;
  accessReviews: number;
};

export const members = {
  useGetMe: (): MFMember | undefined => {
    const data = useMemberGetQuery<MFMember>(["mf", "me"], "/members/me");
    return data.data;
  },
  useGetStats: (): MFMemberStats | undefined => {
    const data = useMemberGetQuery<MFMember>(
      ["mf", "me", "stats"],
      "/members/my-stats",
    );
    return data.data;
  },
  useGetPortalConfig: (): MFPortalConfig | undefined => {
    const data = useMemberGetQuery<MFPortalConfig>(
      ["mf", "portal-config"],
      "/members/portal-config",
    );
    return data.data;
  },
  useGetPortalConfigBySubdomain: (
    subdomain: string,
  ): MFPortalConfig | undefined => {
    const data = useQuery({
      queryKey: ["mf", "portal-config"],
      queryFn: () =>
        fetch(API_BASE + "m/portal-config", {
          method: "GET",
          headers: {
            "content-type": "application/json",
            "x-org-subdomain": subdomain,
          },
        }).then((res) => {
          return res.json();
        }),
    });
    return data.data;
  },
  useHasFeatureFlag: (flag: string): boolean => {
    const member = members.useGetMe();
    return member?.enabledFeatures?.includes(flag) ?? false;
  },
};
